import type {
  ActivityEventView,
  CaseDetail,
  JsonValue,
  PartLineView,
} from "../../../../shared/contracts.js";
import type { CaseStatus, PartException } from "../../../../shared/domain.js";
import { STATUS_PRESENTATION } from "../../../ui/StatusBadge.js";
import {
  EXCEPTION_LABELS,
  etaLabel,
} from "../parts-actions/VerifyArrivalDialog.js";

/**
 * The sentence dictionary behind the timeline (spec §7): one plain sentence
 * per activity event, written from the `changes` and `metadata` the server
 * actually records, with the raw field / previous / next kept as `details`
 * for the Details disclosure.
 *
 * What each event carries (the source of every sentence here):
 * - `case_created` — one change per non-empty header field and one
 *   `partLines` change per line; `metadata.duplicateOverride` when a warning
 *   was overridden.
 * - `case_updated` — one change per header field; `metadata.duplicateOverride`
 *   / `correctionReason` when present.
 * - `case_cancelled` — `status`, `cancellationReason`, sometimes `arrivedAt`.
 * - `part_line_added` — `partNumber`, `description`, `quantityOrdered`, `eta`;
 *   `metadata.lineId`.
 * - `part_line_updated` — the changed line fields; `metadata.lineId`
 *   (and `correctionReason`). The part's name comes from the detail's line.
 * - `part_line_exception_set` / `_cleared` — `exception`, `exceptionReason`;
 *   `metadata.lineId`, `partNumber` (and `reason` on clear).
 * - `quantity_verified` — `quantityVerified`; `metadata.lineId`, `partNumber`,
 *   `quantityReceivedNow`, `confirmed`, and (rows written since the timeline
 *   read them) `description`, `quantityOrdered` as the line stood then.
 * - `verification_reversed` — `quantityVerified`; `metadata.lineId`,
 *   `partNumber`, `reason`.
 * - `status_changed` — `status`, sometimes `arrivedAt`; `metadata.correctionReason`.
 * - `case_arrived` / `arrival_corrected` — `emailEventId`; `metadata.kind`,
 *   `recipientCount`, `recipientWarnings` (and `correctionReason`).
 * - `comment_added` — no changes; `metadata.commentId`.
 * - `email_retry_requested` — no changes; `metadata.emailEventId`, `kind`, …
 *
 * Every value is worded — "no ETA", "Pre-RO", "unassigned", "none", never
 * "—", "null" or JSON in a sentence — and a person is named through the
 * names the server resolved for the event (`event.names`), then the caller's
 * context; an id nobody can name reads "another person" in the sentence and
 * stays raw in the Details row. The wholesale side (`wholesale_*` events)
 * uses the same words: an order is an order; its header fields read as
 * "Customer" (named through `event.names`, which the server fills for a
 * `customerId` change), "Reference", "R&R number" and "ETA for all parts",
 * `wholesale_order_created` counts its lines from `metadata.lineCount`, and
 * `wholesale_email_sent` names the address the customer email went to.
 * Anything unknown falls back to its humanised type so nothing is lost.
 */

export interface ActivityContext {
  side: "service" | "wholesale";
  /** The detail's current lines, so a line-level event can name its part. */
  lines?: readonly PartLineView[];
  /**
   * Names the detail knows for user ids (the current advisor and technician).
   * The names an event carries itself take precedence; these fill in for rows
   * an older server projected without them.
   */
  people?: Readonly<Record<string, string>>;
}

/**
 * The names the sentences may use (spec §7): the detail's lines, so a
 * line-level event reads "Oil filter" rather than a part number, and the
 * current advisor and technician by id, behind the names each event carries.
 */
export function activityContext(detail: CaseDetail): ActivityContext {
  const { case: record } = detail;
  const people: Record<string, string> = { [record.advisorId]: record.advisorName };
  if (record.technicianId !== null && record.technicianName !== null) {
    people[record.technicianId] = record.technicianName;
  }
  return { side: "service", lines: detail.partLines, people };
}

export interface ActivityDetail {
  label: string;
  previous: string;
  next: string;
}

export interface DescribedActivityEvent {
  sentence: string;
  details: readonly ActivityDetail[];
}

/** Product words for the fields the server records; anything else is humanised. */
export const FIELD_LABELS: Readonly<Record<string, string>> = {
  roNumber: "RO",
  vin: "VIN",
  vinEnding: "VIN ending",
  technicianId: "Technician",
  advisorId: "Advisor",
  quantityOrdered: "Quantity",
  quantityVerified: "Verified",
  eta: "ETA",
  etaKind: "ETA",
  customerName: "Customer",
  vehicleYear: "Vehicle year",
  vehicleMake: "Vehicle make",
  vehicleModel: "Vehicle model",
  appointmentDate: "Appointment",
  vendorOrderReference: "Vendor reference",
  orderNote: "Order note",
  carInShop: "Car in shop",
  exception: "Flag",
  exceptionReason: "Flag reason",
  status: "Status",
  description: "Description",
  partNumber: "Part number",
  partLines: "Part line",
  cancellationReason: "Cancellation reason",
  arrivedAt: "Arrived",
  emailEventId: "Email",
  masterEta: "ETA for all parts",
  customerId: "Customer",
  customerReference: "Reference",
  rrNumber: "R&R number",
};

/** The word for an empty value, per field. */
const EMPTY_WORDS: Readonly<Record<string, string>> = {
  roNumber: "Pre-RO",
  technicianId: "unassigned",
  advisorId: "unassigned",
  eta: "no ETA",
  etaKind: "no ETA",
};

const PERSON_FIELDS = new Set(["technicianId", "advisorId"]);
/** A wholesale customer's id: named through `event.names`, like a person. */
const CUSTOMER_FIELDS = new Set(["customerId"]);
/** Fields whose value is one of the server's own ids; worded, never shown. */
const EMAIL_FIELDS = new Set(["emailEventId"]);
const UNNAMED_PERSON = "another person";
const UNNAMED_CUSTOMER = "another customer";
const INSTANT_FIELDS = new Set(["arrivedAt", "cancelledAt", "createdAt", "updatedAt"]);
const DATE_FIELDS = new Set(["appointmentDate", "masterEta"]);

type Change = ActivityEventView["changes"][number];

// ---------------------------------------------------------------------------
// Words for values
// ---------------------------------------------------------------------------

const instantFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Chicago",
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

/** "Sep 16, 2026, 9:05 AM" in the dealership's zone; an unparseable value is shown as-is. */
export function formatInstant(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : instantFormatter.format(date);
}

/** `part_line_exception_set` → "Part line exception set" (the fallback for an unknown event). */
export function humanizeEventType(eventType: string): string {
  const words = eventType.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : eventType;
}

export function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? humanizeFieldName(field);
}

/** `somethingNew` → "Something new". */
function humanizeFieldName(field: string): string {
  const words = field
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : field;
}

type JsonObject = { readonly [key: string]: JsonValue };

/** A JSON object — not null, not an array (`Array.isArray` does not narrow a readonly array). */
function isJsonObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isEtaJson(value: JsonValue): value is { kind: "date"; date: string } | { kind: "no_eta" } {
  return (
    isJsonObject(value) &&
    (value.kind === "no_eta" || (value.kind === "date" && typeof value.date === "string"))
  );
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** "Sep 19, 2026" for a calendar date the server stored as `YYYY-MM-DD`. */
function formatDateOnly(value: string): string {
  return etaLabel({ kind: "date", date: value });
}

/**
 * The word for one value of one field: the field's empty word for null or
 * blank, product labels for statuses and flags, dates and instants in the
 * dealership's zone, names for people the context knows, "yes"/"no" for
 * booleans, and JSON only for anything structured (which never reaches a
 * sentence — see `wordFor`).
 */
export function wordFor(field: string, value: JsonValue, context: ActivityContext): string {
  if (value === null || value === "") return EMPTY_WORDS[field] ?? "none";
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") {
    if (field === "status") return statusWord(value);
    if (field === "exception") return exceptionWord(value);
    if (PERSON_FIELDS.has(field)) return context.people?.[value] ?? UNNAMED_PERSON;
    if (CUSTOMER_FIELDS.has(field)) return context.people?.[value] ?? UNNAMED_CUSTOMER;
    if (EMAIL_FIELDS.has(field)) return "queued";
    if (INSTANT_FIELDS.has(field) && ISO_INSTANT.test(value)) return formatInstant(value);
    if (DATE_FIELDS.has(field) && ISO_DATE.test(value)) return formatDateOnly(value);
    return value;
  }
  if (isEtaJson(value)) return value.kind === "no_eta" ? "no ETA" : formatDateOnly(value.date);
  if (field === "partLines" && isJsonObject(value)) return partLineWord(value) ?? JSON.stringify(value);
  return JSON.stringify(value);
}

/**
 * "4 × Oil filter (15208-9E01A), ETA Sep 19, 2026" for the line object a
 * `case_created` event records per line; null when the object is not one.
 */
function partLineWord(line: JsonObject): string | null {
  if (typeof line.partNumber !== "string" || typeof line.description !== "string") return null;
  const quantity = typeof line.quantityOrdered === "number" ? `${line.quantityOrdered} × ` : "";
  const eta =
    line.eta === undefined || !isEtaJson(line.eta)
      ? ""
      : line.eta.kind === "no_eta"
        ? ", no ETA"
        : `, ETA ${formatDateOnly(line.eta.date)}`;
  return `${quantity}${line.description} (${line.partNumber})${eta}`;
}

function statusWord(status: string): string {
  const presentation = STATUS_PRESENTATION[status as CaseStatus];
  return presentation ? presentation.label : humanizeFieldName(status);
}

function exceptionWord(exception: string): string {
  return EXCEPTION_LABELS[exception as PartException] ?? humanizeFieldName(exception);
}

/** The flag as it reads inside a sentence: "backordered", "wrong part". */
function flagPhrase(exception: JsonValue): string {
  return typeof exception === "string" ? exceptionWord(exception).toLowerCase() : "";
}

/** The flag as a noun before "flag" (spec §7): "the backorder flag", "the wrong-part flag". */
const FLAG_NOUNS: Readonly<Record<PartException, string>> = {
  backordered: "backorder",
  wrong_part: "wrong-part",
  damaged_part: "damaged-part",
  cancelled_line: "cancelled-line",
};

function flagNoun(exception: JsonValue): string | null {
  if (typeof exception !== "string") return null;
  return FLAG_NOUNS[exception as PartException] ?? flagPhrase(exception);
}

function quoted(text: string): string {
  return `'${text}'`;
}

/**
 * Ends a sentence with a full stop — unless it already ends on a quoted
 * reason that carries its own ("…: 'Vendor reports two weeks.'"), so a typed
 * period is never doubled.
 */
function finish(sentence: string): string {
  return /[.!?]'?$/.test(sentence) ? sentence : `${sentence}.`;
}

function plural(count: number, singular: string, pluralWord = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : pluralWord}`;
}

// ---------------------------------------------------------------------------
// Reading an event
// ---------------------------------------------------------------------------

function who(event: ActivityEventView): string {
  return event.actorName ?? "System";
}

function change(event: ActivityEventView, field: string): Change | undefined {
  return event.changes.find((candidate) => candidate.field === field);
}

function metaString(event: ActivityEventView, key: string): string | null {
  const value = event.metadata[key];
  return typeof value === "string" && value !== "" ? value : null;
}

function metaNumber(event: ActivityEventView, key: string): number | null {
  const value = event.metadata[key];
  return typeof value === "number" ? value : null;
}

function nextString(event: ActivityEventView, field: string): string | null {
  const value = change(event, field)?.next;
  return typeof value === "string" && value !== "" ? value : null;
}

/** The detail's line for a line-level event, when the detail still lists it. */
function lineOf(event: ActivityEventView, context: ActivityContext): PartLineView | undefined {
  const lineId = metaString(event, "lineId");
  return lineId === null ? undefined : context.lines?.find((line) => line.id === lineId);
}

/**
 * How a sentence names the part: the detail's current description for the
 * line, else the part number the event carries, else "a part line".
 */
function partLabel(event: ActivityEventView, context: ActivityContext): string {
  const line = lineOf(event, context);
  if (line) return line.description;
  return nextString(event, "description") ?? metaString(event, "partNumber") ?? "a part line";
}

/** The reason the caller typed, when the event carries one under `key`. */
function reasonClause(event: ActivityEventView, key: string): string {
  const reason = metaString(event, key);
  return reason === null ? "" : `: ${quoted(reason)}`;
}

/** The override reason typed past a duplicate warning, when the event carries one. */
function duplicateOverrideReason(event: ActivityEventView): string | null {
  const override = event.metadata.duplicateOverride;
  if (!isJsonObject(override)) return null;
  const reason = override.reason;
  return typeof reason === "string" && reason !== "" ? reason : null;
}

// ---------------------------------------------------------------------------
// Sentences
// ---------------------------------------------------------------------------

function createdSentence(event: ActivityEventView): string {
  const recorded = event.changes.filter((candidate) => candidate.field === "partLines").length;
  // A wholesale order records its line count in the metadata as well.
  const lineCount = recorded > 0 ? recorded : (metaNumber(event, "lineCount") ?? 0);
  const count = lineCount > 0 ? ` (${plural(lineCount, "part")})` : "";
  const override = duplicateOverrideReason(event);
  const overrideClause = override === null ? "" : `, overriding a duplicate warning: ${quoted(override)}`;
  return `${who(event)} created this order${count}${overrideClause}`;
}

function lineAddedSentence(event: ActivityEventView, context: ActivityContext): string {
  const quantity = change(event, "quantityOrdered")?.next;
  const quantityPhrase = typeof quantity === "number" ? `${quantity} × ` : "";
  const line = lineOf(event, context);
  const description = nextString(event, "description") ?? line?.description ?? "a part line";
  const partNumber = nextString(event, "partNumber") ?? line?.partNumber ?? null;
  const number = partNumber === null ? "" : ` (${partNumber})`;
  return `${who(event)} added ${quantityPhrase}${description}${number}`;
}

/** "the ETA from no ETA to Sep 19, 2026" — one clause per changed line field. */
function lineChangeClause(entry: Change, context: ActivityContext): string {
  const noun = LINE_FIELD_NOUNS[entry.field] ?? fieldLabel(entry.field).toLowerCase();
  const quote = entry.field === "description";
  const previous = wordFor(entry.field, entry.previous, context);
  const next = wordFor(entry.field, entry.next, context);
  return `the ${noun} from ${quote ? quoted(previous) : previous} to ${quote ? quoted(next) : next}`;
}

const LINE_FIELD_NOUNS: Readonly<Record<string, string>> = {
  eta: "ETA",
  quantityOrdered: "quantity",
  description: "description",
  partNumber: "part number",
};

function joinClauses(clauses: readonly string[]): string {
  if (clauses.length <= 1) return clauses.join("");
  return `${clauses.slice(0, -1).join(", ")} and ${clauses[clauses.length - 1]}`;
}

function lineUpdatedSentence(event: ActivityEventView, context: ActivityContext): string {
  if (event.changes.length === 0) return `${who(event)} changed ${partLabel(event, context)}`;
  const [first, ...rest] = event.changes;
  const firstClause = lineChangeClause(first!, context).replace(
    / from /,
    ` for ${partLabel(event, context)} from `,
  );
  const clauses = [firstClause, ...rest.map((entry) => lineChangeClause(entry, context))];
  return `${who(event)} changed ${joinClauses(clauses)}`;
}

/**
 * "verified 2 of 4 Oil filter". The ordered quantity and the part's name come
 * from the event when it recorded them, so the sentence stays what was true
 * at the time; only a row written before the server copied them in falls
 * back to the detail's current line.
 */
function verifiedSentence(event: ActivityEventView, context: ActivityContext): string {
  const verified = change(event, "quantityVerified");
  const total = typeof verified?.next === "number" ? verified.next : null;
  const receivedNow = metaNumber(event, "quantityReceivedNow") ?? total;
  const ordered = metaNumber(event, "quantityOrdered") ?? lineOf(event, context)?.quantityOrdered ?? null;
  const part = metaString(event, "description") ?? partLabel(event, context);
  if (ordered !== null && total !== null) {
    return receivedNow === total || receivedNow === null
      ? `${who(event)} verified ${total} of ${ordered} ${part}`
      : `${who(event)} verified ${receivedNow} more ${part} (${total} of ${ordered})`;
  }
  const count = receivedNow ?? total;
  const countPhrase = count === null ? "" : `${count} × `;
  const soFar = total === null ? "" : ` (${total} verified so far)`;
  return `${who(event)} verified ${countPhrase}${part}${soFar}`;
}

function reversedSentence(event: ActivityEventView, context: ActivityContext): string {
  const verified = change(event, "quantityVerified");
  const span =
    verified && typeof verified.previous === "number" && typeof verified.next === "number"
      ? `, from ${verified.previous} to ${verified.next} verified`
      : "";
  return `${who(event)} reversed a verification on ${partLabel(event, context)}${span}${reasonClause(event, "reason")}`;
}

function exceptionSetSentence(event: ActivityEventView, context: ActivityContext): string {
  const flag = change(event, "exception");
  const reason = nextString(event, "exceptionReason");
  const reasonPhrase = reason === null ? "" : `: ${quoted(reason)}`;
  if (flag && typeof flag.next === "string") {
    return `${who(event)} flagged ${partLabel(event, context)} as ${flagPhrase(flag.next)}${reasonPhrase}`;
  }
  return `${who(event)} updated the flag on ${partLabel(event, context)}${reasonPhrase}`;
}

function exceptionClearedSentence(event: ActivityEventView, context: ActivityContext): string {
  const noun = flagNoun(change(event, "exception")?.previous ?? null);
  const flagWords = noun === null ? "flag" : `${noun} flag`;
  return `${who(event)} cleared the ${flagWords} on ${partLabel(event, context)}${reasonClause(event, "reason")}`;
}

/**
 * One sentence per changed header field, in product words. The actor is not
 * repeated in each — the entry's meta line names them once (spec §7).
 */
function headerChangeSentence(entry: Change, context: ActivityContext): string {
  const label = fieldLabel(entry.field);
  const previous = wordFor(entry.field, entry.previous, context);
  const next = wordFor(entry.field, entry.next, context);
  const wasEmpty = entry.previous === null || entry.previous === "";
  const isEmpty = entry.next === null || entry.next === "";
  if (entry.field === "roNumber" && wasEmpty && !isEmpty) return `RO ${next} attached (was Pre-RO)`;
  // A note can run to 2,000 characters; the disclosure carries the text.
  if (entry.field === "orderNote") {
    return wasEmpty ? "Order note added" : isEmpty ? "Order note removed" : "Order note updated";
  }
  // "Car in shop changed from no to yes" is how the generic sentence would
  // read it; this says what actually happened at the counter.
  if (entry.field === "carInShop") {
    return entry.next === true ? "Marked car in shop" : "No longer marked car in shop";
  }
  if (PERSON_FIELDS.has(entry.field)) {
    // The server names everyone an event refers to; only an id nobody can
    // name any more reads as "another person", and a sentence never leans
    // on that twice.
    const previousUnknown = !wasEmpty && previous === UNNAMED_PERSON;
    const nextUnknown = !isEmpty && next === UNNAMED_PERSON;
    if (previousUnknown && isEmpty) return `${label} unassigned`;
    if (previousUnknown && !nextUnknown) return `${label} changed to ${next}`;
    if (previousUnknown && nextUnknown) return `${label} changed`;
  }
  return `${label} changed from ${previous} to ${next}`;
}

function updatedSentence(event: ActivityEventView, context: ActivityContext): string {
  const sentences = event.changes.map((entry) => finish(headerChangeSentence(entry, context)));
  const override = duplicateOverrideReason(event);
  if (override !== null) sentences.push(finish(`Duplicate warning overridden: ${quoted(override)}`));
  return sentences.length > 0 ? sentences.join(" ") : `${who(event)} updated this order`;
}

function statusSentence(event: ActivityEventView, context: ActivityContext): string {
  const status = change(event, "status");
  if (!status) return "Status changed";
  return `Status changed from ${wordFor("status", status.previous, context)} to ${wordFor("status", status.next, context)}`;
}

function cancelledSentence(event: ActivityEventView): string {
  const reason = nextString(event, "cancellationReason");
  return `${who(event)} cancelled this order${reason === null ? "" : `: ${quoted(reason)}`}`;
}

/** "emailed the customer at parts@shop.example (2 parts in)" — the manual wholesale email (spec §5). */
function customerEmailSentence(event: ActivityEventView): string {
  const to = metaString(event, "to");
  const included = metaNumber(event, "partsIncluded");
  const complete = event.metadata.complete === true;
  const parts =
    included === null ? "" : complete ? ` (every part in)` : ` (${plural(included, "part")} in)`;
  return `${who(event)} emailed the customer${to === null ? "" : ` at ${to}`}${parts}`;
}

function emailRetrySentence(event: ActivityEventView): string {
  const kind = metaString(event, "kind");
  const email =
    kind === "arrival"
      ? "the arrival email"
      : kind === "arrival_corrected"
        ? "the arrival-correction email"
        : kind === "wholesale_ready"
          ? "the customer email"
          : "the email";
  return `${who(event)} asked for ${email} to be sent again`;
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * The sentence and the raw detail rows for one event. The sentence is what
 * the timeline shows and what a screen reader reads; `details` carries every
 * change with its label and worded values for the Details disclosure.
 */
export function describeActivityEvent(
  event: ActivityEventView,
  context: ActivityContext,
): DescribedActivityEvent {
  const named = withEventNames(event, context);
  const details: ActivityDetail[] = event.changes.map((entry) => ({
    label: fieldLabel(entry.field),
    previous: detailWord(entry.field, entry.previous, named),
    next: detailWord(entry.field, entry.next, named),
  }));
  return { sentence: finish(sentenceFor(event, named)), details };
}

/** The context plus the names the server resolved for this event, which win. */
function withEventNames(event: ActivityEventView, context: ActivityContext): ActivityContext {
  const names = event.names ?? {};
  if (Object.keys(names).length === 0) return context;
  return { ...context, people: { ...context.people, ...names } };
}

/**
 * A Details row's value: the sentence's word, except that a person nobody
 * can name stays as the raw id — the disclosure is the raw previous / next
 * (spec §7), and "another person" would hide the one fact it carries.
 */
function detailWord(field: string, value: JsonValue, context: ActivityContext): string {
  const word = wordFor(field, value, context);
  const unnamed =
    (PERSON_FIELDS.has(field) && word === UNNAMED_PERSON) || (CUSTOMER_FIELDS.has(field) && word === UNNAMED_CUSTOMER);
  return unnamed && typeof value === "string" ? value : word;
}

function sentenceFor(event: ActivityEventView, context: ActivityContext): string {
  switch (event.eventType) {
    case "case_created":
    case "wholesale_order_created":
      return createdSentence(event);
    case "case_updated":
    case "wholesale_order_updated":
      return updatedSentence(event, context);
    case "case_cancelled":
    case "wholesale_order_cancelled":
      return cancelledSentence(event);
    case "part_line_added":
      return lineAddedSentence(event, context);
    case "part_line_updated":
      return lineUpdatedSentence(event, context);
    case "part_line_exception_set":
      return exceptionSetSentence(event, context);
    case "part_line_exception_cleared":
      return exceptionClearedSentence(event, context);
    case "quantity_verified":
      return verifiedSentence(event, context);
    case "verification_reversed":
      return reversedSentence(event, context);
    case "case_arrived":
    case "wholesale_arrived":
      return "All parts arrived";
    case "arrival_corrected":
      return `Arrival corrected — the order is no longer complete${reasonClause(event, "correctionReason")}`;
    case "status_changed":
      return statusSentence(event, context);
    case "comment_added":
      return `${who(event)} commented`;
    case "email_retry_requested":
      return emailRetrySentence(event);
    case "wholesale_email_sent":
      return customerEmailSentence(event);
    default:
      return `${humanizeEventType(event.eventType)}`;
  }
}
